/* eslint-disable camelcase */
/**
 * NewsFilterController
 *
 * Centralized controller for filtering news content across multiple blocks
 *
 * Manages:
 * - Filter tab changes (category filtering)
 * - Coordinated updates across Primary News, Featured Article, and Additional Grid
 * - Loading states and error handling
 * - Smooth scrolling and animations
 *
 * @package Gravity
 * @since 1.0.0
 */
import LoadingManager from '../utils/LoadingManager.js';

export default class NewsFilterController {
  /**
   * Constructor
   * Initializes controller with default state and cached DOM elements
   */
  constructor() {
    // State
    this.currentCategory = '';
    this.isLoading = false;
    this.isInitialized = false;

    // Cache DOM elements
    this.$primaryNews = $('.gg-primary-news-listing');
    this.$featuredArticle = $('.featured-article-hightlight');
    this.$additionalGrid = $('.gg-additional-post-grid');
    this.clsHightlightMutual = '.featured-article-hightlight--mutual';
    this.clsHightlightNews = '.featured-article-hightlight--news';

    // Configuration
    this.config = {
      ajaxUrl: window.ajaxurl || '/wp-admin/admin-ajax.php',
      // Offset for fixed header
      scrollOffset: 100,
      // Delay before triggering animations
      animationDelay: 100,
      // Smooth scroll duration in ms
      scrollDuration: 600,
    };

    // Debug mode
    this.debug = window.WP_DEBUG || false;
  }

  /**
   * Initialize the controller
   * Sets up event listeners
   */
  init() {
    this.bindEvents();
    this.isInitialized = true;
  }

  /**
   * Bind event listeners
   * Listens to filterTabsChanged custom event from Filter Tabs component
   */
  bindEvents() {
    // Listen to filter tabs change event
    $(document).on('filterTabsChanged', (_e, category, postType) => {

      if (category !== this.currentCategory) {
        this.currentCategory = category;
        this.filterAllBlocks(category, postType);
      }
    });
  }

  /**
   * Filter all blocks based on selected category
   * Makes a single AJAX request to update all blocks simultaneously
   *
   * @param {string} category - Category slug to filter by
   * @param {string} postType - Post type ('news' or 'mutual')
   */
  filterAllBlocks(category, postType = 'news') {
    // Prevent concurrent requests
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    LoadingManager.show();

    // Get configuration from data attributes
    const postsPerPage = parseInt(this.$additionalGrid.data('posts-per-page'), 10) || 6;
    const labelText = this.$featuredArticle.data('label-text') || '';
    const layoutTemplate = this.$featuredArticle.data('layout-template') || 'default';
    const pageId = parseInt(this.$additionalGrid.data('page-id'), 10) || 0;

    // Prepare AJAX data
    const ajaxData = {
      action: 'filter_all_news_blocks',
      category,
      page: 1,
      posts_per_page: postsPerPage,
      post_type: postType,
      label_text: labelText,
      layout_template: layoutTemplate,
      page_id: pageId,
    };

    $.ajax({
      url: this.config.ajaxUrl,
      type: 'POST',
      dataType: 'json',
      data: ajaxData,
      success: (response) => {
        this.handleAjaxResponse(response);
      },
      error: (xhr, status, error) => {
        this.handleAjaxError(xhr, status, error);
      },
      complete: () => {
        this.isLoading = false;
        LoadingManager.hide();
      },
    });
  }

  /**
   * Handle AJAX response
   * Parses response and updates all blocks
   *
   * @param {object|string} response - AJAX response
   */
  handleAjaxResponse(response) {
    let parsedResponse = response;

    // Handle string responses (shouldn't happen, but just in case)
    if (typeof response === 'string') {
      try {
        const jsonMatch = response.match(/\{.*\}$/s);
        if (jsonMatch) {
          parsedResponse = JSON.parse(jsonMatch[0]);
        }
      } catch (e) {
        this.showUserError('Invalid response format. Please refresh and try again.');
        return;
      }
    }

    // Check if response is successful
    if (parsedResponse.success) {
      this.updateAllBlocks(parsedResponse.data);
    } else {
      this.showUserError(parsedResponse.data?.message || 'Error loading filtered content.');
    }
  }

  /**
   * Handle AJAX error
   * Logs error and attempts to extract JSON from malformed response
   *
   * @param {object} xhr - XMLHttpRequest object
   * @param {string} status - Error status
   * @param {string} error - Error message
   */
  handleAjaxError(xhr, status, _error) {

    // Try to extract and parse JSON from response even on error
    if (xhr.responseText && status === 'parsererror') {
      try {
        const jsonMatch = xhr.responseText.match(/\{.*\}$/s);
        if (jsonMatch) {
          const parsedData = JSON.parse(jsonMatch[0]);

          if (parsedData.success) {
            this.updateAllBlocks(parsedData.data);
            return;
          }
        }
      } catch (e) {
        //empty code
      }
    }

    // Show user-friendly error
    this.showUserError('Error loading filtered content. Please refresh the page and try again.');
  }

  /**
   * Update all blocks with filtered content
   *
   * @param {object} data - Response data containing HTML for all blocks
   */
  updateAllBlocks(data) {
    // Update Primary News Listing
    this.updatePrimaryNews(data.primary_news);

    // Update Featured Article Highlight
    this.updateFeaturedArticle(data.featured_article);

    // Update Additional Post Grid
    this.updateAdditionalGrid(data.additional_posts);

    // Scroll to content
    this.scrollToContent();
  }

  /**
   * Update Primary News Listing block
   *
   * @param {object} data - Primary news data with HTML and IDs
   */
  updatePrimaryNews(data) {
    if (!data || !data.html || !data.all_ids || data.all_ids.length === 0) {
      this.$primaryNews.addClass('hidden').hide();
      return;
    }

    const $wrapper = this.$primaryNews.find('.gg-primary-news-listing__wrapper');

    if ($wrapper.length) {
      $wrapper.html(data.html);

      // Trigger animations after a brief delay
      setTimeout(() => {
        $wrapper.find('.anima-bottom').addClass('animated');
      }, this.config.animationDelay);
    }

    // Show the block
    this.$primaryNews.removeClass('hidden').show().css('display', '');
  }

  /**
   * Update Featured Article Highlight block
   *
   * @param {object} data - Featured article data with HTML and post ID
   */
  updateFeaturedArticle(data) {
    if (!data || !data.html || !data.post_id || data.post_id === 0) {
      this.$featuredArticle.addClass('hidden').hide();
      return;
    }

    // Determine which featured article block to update based on post type
    const postType = data.post_type || 'news';
    const postTypeClass = `featured-article-hightlight--${postType.replace(/_/g, '-')}`;
    const $targetBlock = $(`.${postTypeClass}`);

    if (!$targetBlock.length) {
      return;
    }

    const $track = $targetBlock.find('.featured-article-hightlight__ajax-content');
    if ($track.length) {
      $track.html(data.html);
    }

    // Hide all featured article blocks first
    $('.featured-article-hightlight').addClass('hidden').hide();

    // Show only the target block
    $targetBlock.removeClass('hidden').show().css('display', '');

  }

  /**
   * Update Additional Post Grid block
   *
   * @param {object} data - Additional posts data with HTML, IDs, and pagination info
   */
  updateAdditionalGrid(data) {
    const $grid = this.$additionalGrid.find('.gg-additional-post-grid__grid');
    const $content = this.$additionalGrid.find('.gg-additional-post-grid__content');
    const $empty = this.$additionalGrid.find('.gg-additional-post-grid__empty');
    const $loadMore = this.$additionalGrid.find('.gg-additional-post-grid__load-more');
    const $loadMoreWrapper = this.$additionalGrid.find('.gg-additional-post-grid__load-more-wrapper');

    // Always show the module
    this.$additionalGrid.removeClass('hidden').show().css('display', '');

    // Check if we have results
    if (!data || !data.post_ids || data.post_ids.length === 0) {

      // Show empty state, hide content and load more
      if ($content.length) {
        $content.addClass('hidden');
      }
      if ($empty.length) {
        $empty.removeClass('hidden');
      }
      if ($grid.length) {
        $grid.html('');
      }
      if ($loadMore.length) {
        $loadMore.addClass('hidden');
      }
      if ($loadMoreWrapper.length) {
        $loadMoreWrapper.addClass('hidden');
      }

      return;
    }

    // Has results - show content, hide empty state
    if ($content.length) {
      $content.removeClass('hidden');
    }
    if ($empty.length) {
      $empty.addClass('hidden');
    }

    if ($grid.length && data.html) {
      // Update grid content
      $grid.html(data.html);

      // Update max pages data attribute
      this.$additionalGrid.data('max-pages', data.max_pages);

      // Update load more button state
      if ($loadMore.length) {
        $loadMore.data('page', 1);
        $loadMore.data('current-category', this.currentCategory);

        // Show button only if has_more AND max_pages > 1 (consistent with script.js)
        if (data.has_more && data.max_pages > 1) {
          $loadMore.removeClass('hidden');
          if ($loadMoreWrapper.length) {
            $loadMoreWrapper.removeClass('hidden');
          }
        } else {
          $loadMore.addClass('hidden');
          if ($loadMoreWrapper.length) {
            $loadMoreWrapper.addClass('hidden');
          }
        }
      }
    }

  }

  /**
   * Scroll to top of filtered content
   * Provides smooth scrolling to Primary News block
   */
  scrollToContent() {
    if (!this.$primaryNews.length) {
      return;
    }

    const targetPosition = this.$primaryNews.offset().top - this.config.scrollOffset;

    $('html, body').animate({ scrollTop: targetPosition }, this.config.scrollDuration, 'swing');
  }

  /**
   * Show user-friendly error message
   *
   * @param {string} message - Error message to display
   */
  showUserError(message) {
    // You can customize this to use your preferred notification system
    if (typeof window.showNotification === 'function') {
      window.showNotification(message, 'error');
    } else {
      alert(message);
    }
  }

  /**
   * Log message (only in debug mode)
   *
   * @param {string} message - Message to log
   * @param {*} data - Additional data to log
   */

  /**
   * Log error message
   *
   * @param {string} message - Error message
   * @param {*} error - Error object or additional data
   */
}

/**
 * Auto-initialize when DOM is ready
 * Only initializes if filter tabs exist on the page
 */
$(document).ready(() => {
  if ($('.gg-filter-tabs').length > 0) {
    window.newsFilterController = new NewsFilterController();
    window.newsFilterController.init();
  }
});
